/**
 * Button Component - Controller
 * =============================
 */

import type { ISCEService, ITranscludeFunction } from 'angular';
import { hasTranscludedContent } from '../../_base/transclusion';
import {
  INTENT_EMPHASIS,
  INTENT_RING,
  SIZES,
  SHAPES,
  BASE_CLASSES,
  ICONS,
  SPINNER_SVG,
  DEFAULTS,
  ButtonIntent,
  ButtonEmphasis,
  ButtonSize,
  ButtonShape,
  ButtonType,
  ButtonIconType
} from './constants';

export interface ButtonBindings {
  intent?: ButtonIntent;
  emphasis?: ButtonEmphasis;
  size?: ButtonSize;
  shape?: ButtonShape;
  block?: boolean;
  label?: string;
  subtitle?: string;
  leadingIcon?: ButtonIconType;
  trailingIcon?: ButtonIconType;
  disabled?: boolean;
  loading?: boolean;
  type?: ButtonType;
  onClick?: (args: { $event: Event }) => void;
}

interface BindingChanges {
  leadingIcon?: { currentValue: ButtonIconType };
  trailingIcon?: { currentValue: ButtonIconType };
  [key: string]: unknown;
}

export class ButtonController implements ButtonBindings {
  static $inject = ['$sce', '$transclude'];

  private $sce: ISCEService | null;
  private $transclude: ITranscludeFunction | null;

  // Bindings
  intent?: ButtonIntent;
  emphasis?: ButtonEmphasis;
  size?: ButtonSize;
  shape?: ButtonShape;
  block?: boolean;
  label?: string;
  subtitle?: string;
  leadingIcon?: ButtonIconType;
  trailingIcon?: ButtonIconType;
  disabled?: boolean;
  loading?: boolean;
  type?: ButtonType;
  onClick?: (args: { $event: Event }) => void;

  // Estado interno (SVG confiables para ng-bind-html)
  trustedLeadingIcon: unknown = null;
  trustedTrailingIcon: unknown = null;
  trustedSpinner: unknown = null;

  // Estado interno (modo compound: contenido transcluido reemplaza el label)
  hasContent: boolean = false;

  constructor($sce: ISCEService, $transclude: ITranscludeFunction) {
    this.$sce = $sce;
    this.$transclude = $transclude;
    this.handleClick = this.handleClick.bind(this);
  }

  $onInit(): void {
    this.intent = this.intent || DEFAULTS.intent;
    this.emphasis = this.emphasis || DEFAULTS.emphasis;
    this.size = this.size || DEFAULTS.size;
    this.shape = this.shape || DEFAULTS.shape;
    this.type = this.type || DEFAULTS.type;
    this._updateTrustedIcons();
    this.hasContent = hasTranscludedContent(this.$transclude);
  }

  $onChanges(changes: BindingChanges): void {
    if (changes.leadingIcon || changes.trailingIcon) {
      this._updateTrustedIcons();
    }
  }

  $onDestroy(): void {
    this.$sce = null;
    this.$transclude = null;
  }

  private _updateTrustedIcons(): void {
    if (!this.$sce) return;
    this.trustedLeadingIcon = this.$sce.trustAsHtml(ICONS[this.leadingIcon || 'none'] || '');
    this.trustedTrailingIcon = this.$sce.trustAsHtml(ICONS[this.trailingIcon || 'none'] || '');
    this.trustedSpinner = this.$sce.trustAsHtml(SPINNER_SVG);
  }

  // ---- Clases ----

  getButtonClasses(): string[] {
    const intent = this.intent || DEFAULTS.intent;
    const emphasis = this.emphasis || DEFAULTS.emphasis;
    const size = SIZES[this.size || DEFAULTS.size];
    const shape = SHAPES[this.shape || DEFAULTS.shape];

    const classes = [BASE_CLASSES.button, size.container, size.gap, shape];

    if (this.isDisabled) {
      classes.push(BASE_CLASSES.disabled);
    } else {
      classes.push(INTENT_EMPHASIS[intent][emphasis], INTENT_RING[intent]);
    }

    if (this.block) classes.push(BASE_CLASSES.block);

    return classes;
  }

  getContentClasses(): string[] {
    return [this.block ? 'ec-items-center' : 'ec-items-start'];
  }

  getLabelClasses(): string[] {
    return [SIZES[this.size || DEFAULTS.size].label, 'ec-leading-tight'];
  }

  getSubtitleClasses(): string[] {
    return [SIZES[this.size || DEFAULTS.size].subtitle, 'ec-font-normal', 'ec-opacity-80'];
  }

  getIconClasses(): string[] {
    return [SIZES[this.size || DEFAULTS.size].icon];
  }

  // ---- Eventos ----

  handleClick($event: Event): void {
    if (this.isDisabled) {
      $event.preventDefault();
      $event.stopPropagation();
      return;
    }
    if (typeof this.onClick === 'function') {
      this.onClick({ $event });
    }
  }

  // ---- Getters ----

  get isDisabled(): boolean {
    return this.disabled === true || this.loading === true;
  }

  get hasLeadingIcon(): boolean {
    return !!this.leadingIcon && this.leadingIcon !== 'none';
  }

  get hasTrailingIcon(): boolean {
    return !!this.trailingIcon && this.trailingIcon !== 'none';
  }
}

export default ButtonController;
